import { confirmToast } from "../../utils/toast";
import React, { useState, useEffect } from 'react';
import axios from "../../utils/axiosConfig";

const DocumentMaster = () => {
  const initialForm = {
    DocumentName: '',
    Remark: ''
  };

  const [activeTab, setActiveTab] = useState("list");
  const [form, setForm] = useState(initialForm);
  const [documents, setDocuments] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const res = await axios.get("/documents");
      setDocuments(res.data);
    } catch (err) {
      console.error(err);
      alert("Failed to load documents");
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${"/documents"}/${editingId}`, form);
      } else {
        await axios.post("/documents", form);
      }

      alert(editingId ? "Document updated successfully" : "Document saved successfully");
      resetForm();
      fetchDocuments();
      setActiveTab("list");
    } catch (err) {
      alert("Save failed: " + (err.response?.data || err.message));
    }
  };

  const handleEdit = (doc) => {
    setForm({
      DocumentName: doc.DocumentName || '',
      Remark: doc.Remark || ''
    });
    setEditingId(doc.ID);
    setActiveTab("form");
  };

  const handleDelete = async (id) => {
    if (!await confirmToast("Delete this document?")) return;

    try {
      await axios.delete(`${"/documents"}/${id}`);
      fetchDocuments();
      if (editingId === id) resetForm();
    } catch (err) {
      alert("Delete failed");
    }
  };

  const resetForm = () => {
    setForm(initialForm);
    setEditingId(null);
  };

  // Search Filter
  const filteredDocs = documents.filter((doc) => {
    const t = search.toLowerCase();
    if (!t) return true;
    return (
      (doc.DocumentName || '').toLowerCase().includes(t) ||
      (doc.Remark || '').toLowerCase().includes(t)
    );
  });

  // Pagination
  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentDocs = filteredDocs.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredDocs.length / rowsPerPage);

  return (
    <div className="container mt-3">
      <div className="mb-3">
        <h4 className="fw-bold">📄 Document Master</h4>
      </div>

      <div className="card shadow-sm border-0">

        {/* Tabs */}
        <div className="card-header bg-white">
          <ul className="nav nav-tabs card-header-tabs">
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === "list" ? "active" : ""}`}
                onClick={() => setActiveTab("list")}
              >
                Document List
              </button>
            </li>

            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === "form" ? "active" : ""}`}
                onClick={() => {
                  resetForm();
                  setActiveTab("form");
                }}
              >
                {editingId ? "Edit Document" : "Add Document"}
              </button>
            </li>
          </ul>
        </div>

        <div className="card-body">

          {/* ================= LIST TAB ================= */}
          {activeTab === "list" && (
            <>
              <div className="d-flex justify-content-between mb-3">
                <input
                  type="text"
                  className="form-control w-50"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>

              <table className="table table-bordered table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>Document Name</th>
                    <th>Remark</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentDocs.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center py-3">
                        No documents found.
                      </td>
                    </tr>
                  ) : (
                    currentDocs.map((doc) => (
                      <tr key={doc.ID}>
                        <td>{doc.ID}</td>
                        <td>{doc.DocumentName}</td>
                        <td>{doc.Remark}</td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => handleEdit(doc)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(doc.ID)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    Showing {indexOfFirst + 1} to{" "}
                    {Math.min(indexOfLast, filteredDocs.length)} of{" "}
                    {filteredDocs.length} entries
                  </div>

                  <nav>
                    <ul className="pagination mb-0">

                      <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage - 1)}
                        >
                          Previous
                        </button>
                      </li>

                      {[...Array(totalPages)].map((_, index) => (
                        <li
                          key={index}
                          className={`page-item ${currentPage === index + 1 ? "active" : ""}`}
                        >
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(index + 1)}
                          >
                            {index + 1}
                          </button>
                        </li>
                      ))}

                      <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage + 1)}
                        >
                          Next
                        </button>
                      </li>

                    </ul>
                  </nav>
                </div>
              )}
            </>
          )}

          {/* ================= FORM TAB ================= */}
          {activeTab === "form" && (
            <form onSubmit={handleSubmit}>
              <div className="row g-3">

                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Document Name *
                  </label>
                  <input
                    name="DocumentName"
                    value={form.DocumentName}
                    onChange={handleChange}
                    className="form-control"
                    required
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Remark
                  </label>
                  <input
                    name="Remark"
                    value={form.Remark}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>

              </div>

              <div className="mt-4 text-end">
                <button type="submit" className="btn btn-success me-2">
                  {editingId ? "Update" : "Save"}
                </button>

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setActiveTab("list")}
                >
                  Back to List
                </button>
              </div>
            </form>
          )}

        </div>
      </div>
    </div>
  );
};

export default DocumentMaster;
